/**
 * Editor of the internal day cost (€ HT / jour), the setting behind the
 * economic margin. Two surfaces, one behaviour:
 * - the account menu (AppShell), next to « Heures / jour »;
 * - the Finance screen, as a docked panel opened by `?reglage=cout-interne`.
 * Only `manage_settings` (admin) edits; every other role reads the value and
 * its source, or « Non configuré ».
 */
import { useEffect, useId, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DetailPanel, DetailSection, HonestState, InfoHint, PanelFigures } from '../../components/c';
import { toast } from '../../components/ui/Toast';
import { usePermissions } from '../../hooks/usePermissions';
import { fetchProductionSettings, saveInternalDayCost } from '../../lib/api';
import {
  INTERNAL_DAY_COST_CONSUMER_KEYS,
  INTERNAL_DAY_COST_LABEL,
  formatDayCost,
  parseInternalDayCost,
  readInternalDayCost,
  saveErrorMessage,
} from './internalDayCost';
import './internal-day-cost.css';

/** Current value, its source, and whether this user may change it. */
export function useInternalDayCost() {
  const { permissions, isLoading: permissionsLoading } = usePermissions();
  const query = useQuery({
    // Same key and endpoint as « Heures / jour »: one request for both.
    queryKey: ['settings', 'production'],
    queryFn: fetchProductionSettings,
    staleTime: 5 * 60 * 1000,
  });
  return {
    query,
    info: readInternalDayCost(query.data),
    canEdit: permissions.has('manage_settings'),
    permissionsLoading,
  };
}

/** Saves the cost, then refreshes every screen that shows the economic margin. */
export function useSaveInternalDayCost() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: saveInternalDayCost,
    onSuccess: async (_result, value) => {
      const [settings, ...consumers] = INTERNAL_DAY_COST_CONSUMER_KEYS;
      consumers.forEach((queryKey) => { void queryClient.invalidateQueries({ queryKey }); });
      await queryClient.invalidateQueries({ queryKey: settings });
      toast.success(`Coût interne enregistré : ${formatDayCost(value)}`);
    },
  });
}

/** « 450,5 », as typed in French (the parser also reads « 450.5 »). */
function frenchNumber(value: number): string {
  return String(value).replace('.', ',');
}

/** Draft of a new value: validation as the user types, the server bounds included. */
function useDraft(current: number | null) {
  const [draft, setDraft] = useState('');
  const parsed = draft.trim() ? parseInternalDayCost(draft) : null;
  const ready = parsed?.ok === true && parsed.value !== current;
  return { draft, setDraft, parsed, ready };
}

/* ───────────────────────── Menu de compte ───────────────────────── */

export function InternalDayCostMenuSetting() {
  const { query, info, canEdit } = useInternalDayCost();
  const save = useSaveInternalDayCost();
  const { draft, setDraft, parsed, ready } = useDraft(info.value);
  const hintId = useId();

  if (query.isError) {
    return (
      <div className="shell__system-setting idc-menu">
        <span className="idc-menu__label">{INTERNAL_DAY_COST_LABEL}</span>
        <span className="idc-menu__value idc-menu__value--alert">Réglage non lu</span>
        <button type="button" className="button button--ghost" onClick={() => void query.refetch()}>Réessayer</button>
      </div>
    );
  }

  if (!canEdit) {
    return (
      <div className="shell__system-setting idc-menu">
        <span className="idc-menu__label">{INTERNAL_DAY_COST_LABEL}</span>
        <span className="idc-menu__value num">{query.isLoading ? 'Lecture…' : info.valueLabel}</span>
        {info.value !== null ? <span className="idc-menu__source">{`Source : ${info.sourceLabel.toLowerCase()}`}</span> : null}
      </div>
    );
  }

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!ready || !parsed?.ok) return;
    save.mutate(parsed.value, { onSuccess: () => setDraft('') });
  };
  const value = draft !== '' ? draft : info.value !== null ? frenchNumber(info.value) : '';
  const error = parsed && !parsed.ok ? parsed.error : save.isError ? saveErrorMessage(save.error) : '';

  return (
    <form className="shell__system-setting idc-menu" onSubmit={submit} noValidate>
      <label>
        {INTERNAL_DAY_COST_LABEL}
        <input
          type="text"
          inputMode="decimal"
          value={value}
          placeholder={query.isLoading ? '…' : 'Non configuré'}
          onChange={(event) => {
            if (save.isError) save.reset();
            setDraft(event.target.value);
          }}
          aria-label="Coût interne en euros HT par jour"
          aria-invalid={Boolean(parsed && !parsed.ok)}
          aria-describedby={hintId}
        />
      </label>
      <button type="submit" className="button button--ghost" disabled={save.isPending || !ready}>
        {save.isPending ? '…' : 'OK'}
      </button>
      <span id={hintId} className="idc-menu__source">
        {query.isLoading ? 'Lecture…' : `Source : ${info.sourceLabel.toLowerCase()}`}
      </span>
      {error ? <span className="shell__system-setting-error idc-menu__error" role="alert">{error}</span> : null}
    </form>
  );
}

/* ───────────────────────── Panneau Finance ───────────────────────── */

export const INTERNAL_DAY_COST_PANEL_ID = 'fin-c-internal-day-cost';

export function InternalDayCostPanel({ onClose }: { onClose: () => void }) {
  const { query, info, canEdit, permissionsLoading } = useInternalDayCost();
  const save = useSaveInternalDayCost();
  const { draft, setDraft, parsed, ready } = useDraft(info.value);
  const formId = useId();
  const errorId = useId();
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (canEdit) inputRef.current?.focus();
  }, [canEdit]);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!ready || !parsed?.ok) return;
    save.mutate(parsed.value, {
      onSuccess: () => {
        setDraft('');
        onClose();
      },
    });
  };

  // Escape in the field clears the entry; the panel closes on the next one.
  const onFieldKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape' && draft) {
      event.stopPropagation();
      setDraft('');
      save.reset();
    }
  };

  const error = parsed && !parsed.ok ? parsed.error : save.isError ? saveErrorMessage(save.error) : '';

  return (
    <DetailPanel
      id={INTERNAL_DAY_COST_PANEL_ID}
      label="Réglage du coût interne"
      eyebrow="Réglage"
      title="Coût interne"
      onClose={onClose}
      actions={canEdit ? (
        <button type="submit" form={formId} className="button button--primary" disabled={save.isPending || !ready}>
          {save.isPending ? 'Enregistrement…' : 'Enregistrer le coût interne'}
        </button>
      ) : undefined}
    >
      {query.isLoading ? <p className="idc-note" role="status">Lecture du réglage…</p> : null}
      {query.isError ? (
        <HonestState
          title="Réglage non lu"
          detail="Le serveur n’a pas renvoyé le coût interne"
          action={<button type="button" className="button button--compact" onClick={() => void query.refetch()}>Réessayer</button>}
        />
      ) : null}
      {query.data ? (
        <PanelFigures
          items={[
            { label: 'Coût actuel', value: info.value === null ? 'Non configuré' : formatDayCost(info.value), tone: info.value === null ? 'alert' : undefined },
            { label: 'Source', value: info.sourceLabel },
          ]}
        />
      ) : null}
      {canEdit ? (
        <DetailSection
          title={info.value === null ? 'Renseigner le coût' : 'Nouveau coût'}
          aside={(
            <InfoHint label="Ce que change le coût interne">
              La marge économique retranche de la marge contributive les jours produits × ce coût, pour tout
              l’exercice. Une valeur enregistrée ici prime sur la variable d’environnement du serveur, qui ne sert
              plus que de repli.
            </InfoHint>
          )}
        >
          <form id={formId} className="idc-form" onSubmit={submit} noValidate>
            <label className="idc-form__label">
              {INTERNAL_DAY_COST_LABEL}
              <input
                ref={inputRef}
                className="idc-form__input num"
                type="text"
                inputMode="decimal"
                value={draft}
                placeholder={info.value !== null ? frenchNumber(info.value) : 'Par exemple 450'}
                onChange={(event) => {
                  if (save.isError) save.reset();
                  setDraft(event.target.value);
                }}
                onKeyDown={onFieldKeyDown}
                aria-invalid={Boolean(error)}
                aria-describedby={error ? errorId : undefined}
              />
            </label>
            {error ? <p id={errorId} className="idc-form__error" role="alert">{error}</p> : null}
          </form>
        </DetailSection>
      ) : !permissionsLoading ? (
        <HonestState
          title="Réservé à l’administrateur"
          detail={info.value === null
            ? 'Seul un administrateur peut renseigner le coût interne ; la marge économique s’affichera ensuite.'
            : 'Seul un administrateur peut modifier le coût interne.'}
        />
      ) : null}
    </DetailPanel>
  );
}
